import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import CookieBanner from '../../components/feature/CookieBanner';
import BackToTop from '../../components/feature/BackToTop';
import { injectSchema, getServiceSchema, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seoSchema';

export default function ServicesPage() {
  useEffect(() => {
    document.title = 'Servicios de Limpieza Post Incendio Barcelona | FIRETRAUMAS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios profesionales de limpieza post incendio en Barcelona: eliminación de hollín, neutralización de olores, retirada de cenizas, desinfección y preparación para reformas.');
    }

    // Inject Schema.org JSON-LD
    const cleanupService = injectSchema(getServiceSchema());
    const cleanupBreadcrumb = injectSchema(getBreadcrumbSchema([
      { name: 'Inicio', url: '/' },
      { name: 'Servicios', url: '/servicios' }
    ]));
    const cleanupWebPage = injectSchema(getWebPageSchema(
      'Servicios de Limpieza Post Incendio Barcelona',
      'Servicios profesionales de limpieza post incendio en Barcelona: eliminación de hollín, neutralización de olores, retirada de cenizas, desinfección y preparación para reformas.',
      '/servicios'
    ));

    return () => {
      cleanupService?.();
      cleanupBreadcrumb?.();
      cleanupWebPage?.();
    };
  }, []);

  const services = [
    {
      id: 'hollin',
      number: '01',
      title: 'Limpieza de Hollín y Residuos de Humo',
      description: 'El hollín y los residuos de humo se adhieren a paredes, techos, suelos y objetos. Si no se eliminan correctamente, pueden oxidar superficies y dejar manchas permanentes. Utilizamos técnicas específicas según el material afectado.',
      tasks: [
        'Aspirado HEPA para evitar dispersión de partículas',
        'Lavado técnico con productos pH-neutro según material',
        'Limpieza de paredes, techos y suelos',
        'Tratamiento de superficies porosas y no porosas',
        'Documentación fotográfica antes y después'
      ],
      image: 'https://public.readdy.ai/ai/img_res/01e11d71fdc5b2612626a1bf45c15584.jpg'
    },
    {
      id: 'olores',
      number: '02',
      title: 'Neutralización de Olores Persistentes',
      description: 'El olor a humo puede penetrar en materiales porosos como textiles, madera y yeso. Utilizamos tratamientos de desodorización profesionales para neutralizar olores en profundidad, no solo enmascararlos.',
      tasks: [
        'Evaluación de penetración del olor',
        'Ventilación controlada del espacio',
        'Aplicación de neutralizadores profesionales',
        'Tratamiento de textiles y tapicerías',
        'Seguimiento post-tratamiento'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20odor%20treatment%20specialist%20using%20fogging%20equipment%20in%20modern%20Barcelona%20apartment%20clean%20bright%20interior&width=800&height=600&seq=service-olores-002&orientation=landscape'
    },
    {
      id: 'cenizas',
      number: '03',
      title: 'Retirada de Cenizas, Escombros y Enseres Dañados',
      description: 'Gestionamos la retirada segura de cenizas, escombros y enseres dañados. Coordinamos con el perito del seguro para documentar todo antes de proceder. No tiramos nada sin tu aprobación.',
      tasks: [
        'Documentación fotográfica de enseres dañados',
        'Clasificación de objetos salvables y no salvables',
        'Embalaje seguro de objetos recuperables',
        'Retirada y gestión de residuos según normativa',
        'Coordinación con perito y aseguradora'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20team%20removing%20fire%20damaged%20items%20from%20apartment%20organized%20sorting%20protective%20equipment%20Barcelona%20building&width=800&height=600&seq=service-cenizas-003&orientation=landscape'
    },
    {
      id: 'cocinas',
      number: '04',
      title: 'Limpieza Profunda de Cocinas Quemadas',
      description: 'Las cocinas requieren atención especial por la combinación de hollín con grasa. Limpiamos campanas, azulejos, electrodomésticos y superficies con productos específicos para eliminar residuos grasos carbonizados.',
      tasks: [
        'Desengrase profundo de campanas y extractores',
        'Limpieza de azulejos y juntas',
        'Tratamiento de superficies de acero inoxidable',
        'Limpieza de electrodomésticos salvables',
        'Desinfección completa del área'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20deep%20cleaning%20kitchen%20after%20fire%20specialist%20cleaning%20range%20hood%20tiles%20modern%20Barcelona%20apartment%20restoration&width=800&height=600&seq=service-cocinas-004&orientation=landscape'
    },
    {
      id: 'desinfeccion',
      number: '05',
      title: 'Desinfección y Saneamiento',
      description: 'Tras un incendio, el espacio puede contener bacterias, moho y partículas nocivas. Realizamos una desinfección completa y controlamos la ventilación para garantizar un ambiente seguro antes de que vuelvas.',
      tasks: [
        'Desinfección de todas las superficies',
        'Control de humedad y ventilación',
        'Prevención de moho y hongos',
        'Saneamiento de sistemas de ventilación',
        'Certificación de habitabilidad (si procede)'
      ],
      image: 'https://readdy.ai/api/search-image?query=professional%20disinfection%20service%20in%20clean%20bright%20apartment%20specialist%20using%20equipment%20safety%20protocols%20Barcelona%20interior&width=800&height=600&seq=service-desinfeccion-005&orientation=landscape'
    },
    {
      id: 'preparacion',
      number: '06',
      title: 'Preparación para Pintura y Reformas',
      description: 'Dejamos el espacio limpio y preparado para que pintores y reformistas puedan trabajar. Incluye checklist final de entrega y documentación completa para tu tranquilidad y la del seguro.',
      tasks: [
        'Limpieza final de polvo y residuos',
        'Preparación de superficies para pintura',
        'Checklist de entrega detallado',
        'Documentación fotográfica final',
        'Coordinación con equipo de reformas'
      ],
      image: 'https://readdy.ai/api/search-image?query=clean%20prepared%20apartment%20interior%20ready%20for%20painting%20white%20walls%20clean%20floors%20bright%20natural%20light%20Barcelona%20modern%20space&width=800&height=600&seq=service-preparacion-006&orientation=landscape'
    }
  ];

  const processSteps = [
    { icon: 'ri-phone-line', title: 'Contacto', description: 'Evaluación inicial' },
    { icon: 'ri-search-eye-line', title: 'Inspección', description: 'Presupuesto' },
    { icon: 'ri-shield-line', title: 'Protección', description: 'Contención' },
    { icon: 'ri-tools-line', title: 'Limpieza', description: 'Técnica profesional' },
    { icon: 'ri-checkbox-circle-line', title: 'Entrega', description: 'Checklist final' }
  ];

  const equipment = [
    { icon: 'ri-air-condition-line', name: 'Aspiradores HEPA', description: 'Filtración de partículas finas' },
    { icon: 'ri-mist-line', name: 'Generadores de Ozono', description: 'Neutralización de olores' },
    { icon: 'ri-shield-check-line', name: 'EPIs Certificados', description: 'Protección del equipo' },
    { icon: 'ri-drop-line', name: 'Limpiadores pH-Neutro', description: 'Según tipo de material' },
    { icon: 'ri-temp-cold-line', name: 'Medidores de Humedad', description: 'Control ambiental' },
    { icon: 'ri-foggy-line', name: 'Termonebulizadores', description: 'Desodorización profunda' }
  ];

  return (
    <div className="min-h-screen bg-bg">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-white text-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-serif font-bold text-5xl md:text-6xl text-primary mb-6">
              Servicios de limpieza post incendio
            </h1>
            <p className="text-textLight text-xl leading-relaxed">
              Desde la primera inspección hasta la entrega final. Adaptamos cada intervención a la gravedad del siniestro y tus necesidades específicas.
            </p>
          </div>
        </section>

        {/* Services Deep Dive */}
        {services.map((service, index) => (
          <section
            key={service.id}
            id={service.id}
            className={`py-20 ${index % 2 === 0 ? 'bg-bg' : 'bg-white'}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <span className="text-secondary uppercase text-xs tracking-wider font-sans font-semibold">
                    Servicio {service.number}
                  </span>
                  <h2 className="font-serif font-bold text-4xl text-primary mt-4 mb-6">
                    {service.title}
                  </h2>
                  <p className="text-textLight text-lg leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.tasks.map((task, taskIndex) => (
                      <li key={taskIndex} className="flex items-start gap-3">
                        <i className="ri-check-line text-accent text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-textPrimary">{task}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/contacto#form"
                    className="inline-block text-secondary font-sans font-semibold hover:text-primary transition-colors cursor-pointer"
                  >
                    Solicitar presupuesto
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Process Flowchart */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif font-bold text-4xl text-primary mb-6">
                Nuestro proceso de trabajo
              </h2>
              <p className="text-textLight text-lg max-w-2xl mx-auto">
                Cinco pasos claros desde el primer contacto hasta la entrega final.
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center relative">
                  <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mb-4 shadow-md">
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="font-sans font-bold text-lg text-primary mb-1">
                    {step.title}
                  </h4>
                  <p className="text-textLight text-sm">{step.description}</p>
                  {index < processSteps.length - 1 && (
                    <i className="ri-arrow-right-line text-3xl text-secondary absolute top-8 -right-12 hidden md:block"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Equipment Showcase */}
        <section className="py-20 bg-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif font-bold text-4xl text-primary mb-6">
                Equipamiento profesional
              </h2>
              <p className="text-textLight text-lg max-w-2xl mx-auto">
                Utilizamos equipos especializados para garantizar resultados óptimos y seguridad.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {equipment.map((item, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-2xl text-secondary`}></i>
                  </div>
                  <h4 className="font-sans font-bold text-lg text-primary mb-2">
                    {item.name}
                  </h4>
                  <p className="text-textLight text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Insurance Coordination */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-serif font-bold text-4xl text-primary mb-6">
                Trabajamos con tu seguro y perito
              </h2>
              <p className="text-textLight text-lg max-w-2xl mx-auto">
                Facilitamos todo el proceso de reclamación con tu aseguradora. Consulta más información en nuestra <a href="/faq#seguros" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">sección de preguntas sobre seguros</a>.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-bg rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-camera-line text-2xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-4">Antes</h3>
                <p className="text-textLight">
                  Documentamos todos los daños con fotos y vídeo detallados antes de iniciar cualquier trabajo.
                </p>
              </div>
              <div className="bg-bg rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-4">Durante</h3>
                <p className="text-textLight">
                  Coordinamos con el perito, facilitamos informes técnicos y mantenemos comunicación constante.
                </p>
              </div>
              <div className="bg-bg rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <h3 className="font-sans font-bold text-xl text-primary mb-4">Después</h3>
                <p className="text-textLight">
                  Entregamos factura detallada y documentación completa para tu reclamación al seguro.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA to FAQ */}
        <section className="py-20 bg-bg">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-serif font-bold text-4xl text-primary mb-6">
              ¿Tienes más preguntas?
            </h2>
            <p className="text-textLight text-lg mb-8">
              Consulta nuestra <a href="/faq" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">sección de preguntas frecuentes</a> o contáctanos directamente. También puedes conocer más <a href="/empresa" className="text-secondary font-semibold hover:text-primary transition-colors cursor-pointer">sobre nuestra empresa y valores</a>.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/faq"
                className="bg-primary text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer"
              >
                Ver preguntas frecuentes
              </a>
              <a
                href="/contacto"
                className="bg-secondary text-white px-8 py-4 rounded-full font-sans font-semibold hover:bg-opacity-90 transition-all whitespace-nowrap cursor-pointer"
              >
                Solicitar presupuesto
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
      <CookieBanner />
      <BackToTop />
    </div>
  );
}